import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Users, Plus, Pencil, Trash2, Clock, DollarSign, UserCheck, UserX, Timer, AlertTriangle, Download, Key, Mail, CalendarDays, X, Check, MessageSquare, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { BulkDeleteBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '../../components/BulkDelete';
import { EmailScheduleModal } from '../../components/EmailScheduleModal';
import { Pagination, paginate, reconcilePageAfterDelete } from '../../components/Pagination';
import { ActionMenu } from '../../components/crm/CrmListShell';
import useMessageStream from '../../hooks/useMessageStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { party_host: 'Party Host', manager: 'Manager', staff: 'Staff' };
const ROLE_COLORS = { party_host: '#FF3B6B', manager: '#6366F1', staff: '#06D6A0' };
const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time', casual: 'Casual / On-call' };
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const todayIso = () => new Date().toISOString().split('T')[0];

export const CrmEmployees = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [activeTab, setActiveTab] = useState('employees');
  const [flags, setFlags] = useState(null);
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [adminReason, setAdminReason] = useState('');
  const [deleteEntryOpen, setDeleteEntryOpen] = useState(false);
  const [deleteEntryId, setDeleteEntryId] = useState(null);
  const [deleteEntryReason, setDeleteEntryReason] = useState('');
  const [autoClockoutHour, setAutoClockoutHour] = useState(19);
  const [filterTimeEmp, setFilterTimeEmp] = useState('all');
  // Time-entries date range
  const [timePreset, setTimePreset] = useState('last7');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  // Availability tab state
  const [availPreset, setAvailPreset] = useState('next7');
  const [availFrom, setAvailFrom] = useState('');
  const [availTo, setAvailTo] = useState('');
  const [availRoleFilter, setAvailRoleFilter] = useState('all');
  const [availData, setAvailData] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [cellDialogOpen, setCellDialogOpen] = useState(false);
  const [cellDialog, setCellDialog] = useState(null);
  const [timeOffReason, setTimeOffReason] = useState('');
  // Payroll tab state
  const [payrollPreset, setPayrollPreset] = useState('lastMonth');
  const [payrollFrom, setPayrollFrom] = useState('');
  const [payrollTo, setPayrollTo] = useState('');
  const [payrollEmp, setPayrollEmp] = useState('all');
  const [payrollRoleFilter, setPayrollRoleFilter] = useState('all');
  const [payrollDeduction, setPayrollDeduction] = useState('15');
  const [payrollData, setPayrollData] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [paystubs, setPaystubs] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [genType, setGenType] = useState('weekly');
  const [genPeriodEnd, setGenPeriodEnd] = useState('');
  const [genNotes, setGenNotes] = useState('');
  const [genPreview, setGenPreview] = useState(null);
  const [selectedPaystubs, setSelectedPaystubs] = useState([]);
  // Filters
  const [filterEmpType, setFilterEmpType] = useState('all');
  const [filterDay, setFilterDay] = useState('all');
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // Pagination (Employees table)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Create form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [empRole, setEmpRole] = useState('staff');
  const [password, setPassword] = useState('Employee123!');
  const [hourlyRate, setHourlyRate] = useState('15');
  const [pin, setPin] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [dob, setDob] = useState('2000-01-01');
  const [employmentType, setEmploymentType] = useState('full_time');
  const [addrStreet, setAddrStreet] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrProvince, setAddrProvince] = useState('');
  const [addrPostal, setAddrPostal] = useState('');
  const [availability, setAvailability] = useState(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);

  const toggleDay = (setter, value, day) => {
    if (value.includes(day)) setter(value.filter(d => d !== day));
    else setter([...value, day]);
  };

  // ---------- Date range helpers ----------
  const fmtDate = (d) => d.toISOString().split('T')[0];
  const computePastRange = (preset) => {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);
    if (preset === 'today') {
      // start = end = today
    } else if (preset === 'last7') {
      start.setDate(today.getDate() - 6);
    } else if (preset === 'thisWeek') {
      const dow = (today.getDay() + 6) % 7; // Monday = 0
      start.setDate(today.getDate() - dow);
      end.setDate(start.getDate() + 6);
    } else if (preset === 'thisMonth') {
      start.setDate(1);
      end.setMonth(today.getMonth() + 1);
      end.setDate(0);
    } else if (preset === 'lastMonth') {
      start.setMonth(today.getMonth() - 1, 1);
      end.setMonth(today.getMonth(), 0);
    } else {
      return { start: null, end: null };
    }
    return { start: fmtDate(start), end: fmtDate(end) };
  };
  const computeFutureRange = (preset) => {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);
    if (preset === 'next7') {
      end.setDate(today.getDate() + 6);
    } else if (preset === 'nextWeek') {
      const dow = (today.getDay() + 6) % 7;
      start.setDate(today.getDate() + (7 - dow)); // next Monday
      end.setDate(start.getDate() + 6);
    } else if (preset === 'nextMonth') {
      end.setDate(today.getDate() + 29);
    } else {
      return { start: null, end: null };
    }
    return { start: fmtDate(start), end: fmtDate(end) };
  };

  // Sync time-entries preset → dates
  useEffect(() => {
    if (timePreset === 'custom') return;
    const r = computePastRange(timePreset);
    if (r.start) { setTimeFrom(r.start); setTimeTo(r.end); }
  }, [timePreset]);

  // Sync availability preset → dates
  useEffect(() => {
    if (availPreset === 'custom') return;
    const r = computeFutureRange(availPreset);
    if (r.start) { setAvailFrom(r.start); setAvailTo(r.end); }
  }, [availPreset]);

  // Refetch time entries when date range / employee filter changes
  useEffect(() => {
    if (activeTab !== 'time' || !timeFrom || !timeTo) return;
    (async () => {
      try {
        const params = new URLSearchParams({ date_from: timeFrom, date_to: timeTo });
        if (filterTimeEmp !== 'all') params.append('user_id', filterTimeEmp);
        const r = await axios.get(`${API_URL}/api/admin/time-entries?${params}`, { withCredentials: true });
        setTimeEntries(r.data);
      } catch { /* silent */ }
    })();
  }, [activeTab, timeFrom, timeTo, filterTimeEmp]);

  // Fetch availability matrix
  const fetchAvailability = async () => {
    if (!availFrom || !availTo) return;
    setAvailLoading(true);
    try {
      const params = new URLSearchParams({ start_date: availFrom, end_date: availTo });
      if (availRoleFilter !== 'all') params.append('role', availRoleFilter);
      const r = await axios.get(`${API_URL}/api/admin/availability?${params}`, { withCredentials: true });
      setAvailData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load availability');
    } finally { setAvailLoading(false); }
  };
  useEffect(() => {
    if (activeTab !== 'availability') return;
    if (availFrom && availTo) fetchAvailability();
  }, [activeTab, availFrom, availTo, availRoleFilter]);

  const markTimeOff = async () => {
    if (!cellDialog) return;
    try {
      await axios.post(`${API_URL}/api/admin/time-off`,
        { employee_id: cellDialog.user_id, date: cellDialog.date, reason: timeOffReason.trim() },
        { withCredentials: true });
      toast.success('Marked unavailable');
      setCellDialogOpen(false); setTimeOffReason('');
      fetchAvailability();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const clearTimeOff = async () => {
    if (!cellDialog?.cell?.time_off?.time_off_id) return;
    try {
      await axios.delete(`${API_URL}/api/admin/time-off/${cellDialog.cell.time_off.time_off_id}`, { withCredentials: true });
      toast.success('Availability restored');
      setCellDialogOpen(false);
      fetchAvailability();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportAvailabilityCSV = () => {
    if (!availData) return;
    const headers = ['Employee', 'Role', ...availData.dates];
    const rows = availData.employees.map(r => {
      const cells = r.cells.map(c => c.status).map(s =>
        s === 'available' ? 'Available' :
        s === 'busy' ? 'Busy' :
        s === 'time_off' ? 'Time Off' : 'Unavailable'
      );
      return [`"${r.name}"`, r.employee_role || '', ...cells];
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `availability_${availFrom}_${availTo}.csv`; a.click();
    toast.success('CSV exported');
  };

  // Sync payroll preset → dates
  useEffect(() => {
    if (payrollPreset === 'custom') return;
    const r = computePastRange(payrollPreset);
    if (r.start) { setPayrollFrom(r.start); setPayrollTo(r.end); }
  }, [payrollPreset]);

  // Recompute payroll when filters change
  useEffect(() => {
    if (activeTab !== 'payroll' || !payrollFrom || !payrollTo) return;
    (async () => {
      setPayrollLoading(true);
      try {
        const body = {
          date_from: payrollFrom,
          date_to: payrollTo,
          deduction_pct: parseFloat(payrollDeduction) || 0,
        };
        if (payrollEmp !== 'all') body.employee_id = payrollEmp;
        if (payrollRoleFilter !== 'all') body.role = payrollRoleFilter;
        const r = await axios.post(`${API_URL}/api/admin/payroll/compute`, body, { withCredentials: true });
        setPayrollData(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      finally { setPayrollLoading(false); }
    })();
  }, [activeTab, payrollFrom, payrollTo, payrollEmp, payrollRoleFilter, payrollDeduction]);

  // Fetch paystubs whenever we're on payroll tab
  useEffect(() => {
    if (activeTab !== 'payroll') return;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (payrollEmp !== 'all') params.append('employee_id', payrollEmp);
        const r = await axios.get(`${API_URL}/api/admin/paystubs?${params}`, { withCredentials: true });
        setPaystubs(r.data || []);
      } catch { /* silent */ }
    })();
  }, [activeTab, payrollEmp, genOpen]);

  const markPaid = async (row) => {
    try {
      await axios.post(`${API_URL}/api/admin/payroll/mark-paid`,
        { user_id: row.user_id, date_from: payrollFrom, date_to: payrollTo },
        { withCredentials: true });
      toast.success(`${row.name} marked paid`);
      // Trigger recompute
      setPayrollData(prev => prev ? ({ ...prev, rows: prev.rows.map(r => r.user_id === row.user_id ? { ...r, paid_status: true, paid_at: new Date().toISOString() } : r) }) : prev);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const unmarkPaid = async (row) => {
    try {
      await axios.post(`${API_URL}/api/admin/payroll/unmark-paid`,
        { user_id: row.user_id, date_from: payrollFrom, date_to: payrollTo },
        { withCredentials: true });
      setPayrollData(prev => prev ? ({ ...prev, rows: prev.rows.map(r => r.user_id === row.user_id ? { ...r, paid_status: false, paid_at: null } : r) }) : prev);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportPayrollCSV = async () => {
    try {
      const body = {
        date_from: payrollFrom,
        date_to: payrollTo,
        deduction_pct: parseFloat(payrollDeduction) || 0,
      };
      if (payrollEmp !== 'all') body.employee_id = payrollEmp;
      if (payrollRoleFilter !== 'all') body.role = payrollRoleFilter;
      const r = await axios.post(`${API_URL}/api/admin/payroll/export-csv`, body, { withCredentials: true, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `payroll_${payrollFrom}_${payrollTo}.csv`;
      a.click();
      toast.success('CSV exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const previewPaystubs = async () => {
    if (!genPeriodEnd) { toast.error('Pick a period-end date'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/admin/paystubs/generate`,
        { period_type: genType, period_end: genPeriodEnd, deduction_pct: parseFloat(payrollDeduction) || 0, notes: genNotes.trim() || null, preview_only: true, include_zero_hours: false },
        { withCredentials: true });
      setGenPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const generatePaystubs = async () => {
    if (!genPeriodEnd) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/paystubs/generate`,
        { period_type: genType, period_end: genPeriodEnd, deduction_pct: parseFloat(payrollDeduction) || 0, notes: genNotes.trim() || null, preview_only: false, include_zero_hours: false },
        { withCredentials: true });
      toast.success(`${r.data.count} paystub(s) generated`);
      setGenOpen(false); setGenPreview(null); setGenPeriodEnd(''); setGenNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendPaystub = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/paystubs/${id}/send`, {}, { withCredentials: true });
      toast.success('Sent');
      setPaystubs(prev => prev.map(p => p.paystub_id === id ? { ...p, status: 'sent', email_sent_at: new Date().toISOString() } : p));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendSelectedPaystubs = async () => {
    if (selectedPaystubs.length === 0) { toast.error('Select at least one paystub'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/admin/paystubs/send-bulk`, { paystub_ids: selectedPaystubs }, { withCredentials: true });
      toast.success(`${r.data.sent} sent${r.data.failed.length ? `, ${r.data.failed.length} failed` : ''}`);
      setSelectedPaystubs([]);
      // Refresh
      const params = new URLSearchParams();
      if (payrollEmp !== 'all') params.append('employee_id', payrollEmp);
      const res = await axios.get(`${API_URL}/api/admin/paystubs?${params}`, { withCredentials: true });
      setPaystubs(res.data || []);
    } catch (e) { toast.error('Failed'); }
  };

  const downloadPaystubPdf = (id) => {
    window.open(`${API_URL}/api/admin/paystubs/${id}/pdf`, '_blank');
  };

  const toggleSelectPaystub = (id) => {
    setSelectedPaystubs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const runEndOfPeriod = async () => {
    const type = window.prompt('Pay period type? Type "weekly" or "biweekly"', 'weekly');
    if (!type || !['weekly', 'biweekly'].includes(type.toLowerCase())) return;
    const end = window.prompt('Period end date (YYYY-MM-DD)?', new Date().toISOString().split('T')[0]);
    if (!end) return;
    if (!window.confirm(`Run end-of-period for ${type} ending ${end}?\n\nThis will:\n1) Generate paystubs\n2) Email each employee\n3) Mark the period as Paid\n\nContinue?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/payroll/run-end-of-period`,
        { period_type: type.toLowerCase(), period_end: end, deduction_pct: parseFloat(payrollDeduction) || 0 },
        { withCredentials: true });
      toast.success(`Generated ${r.data.generated_count} paystubs · Emailed ${r.data.emailed_count} · Paid ${r.data.marked_paid_count}`);
      // Refresh
      const params = new URLSearchParams();
      if (payrollEmp !== 'all') params.append('employee_id', payrollEmp);
      const res = await axios.get(`${API_URL}/api/admin/paystubs?${params}`, { withCredentials: true });
      setPaystubs(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Live cross-tab refresh: when an employee/shift/paystub is deleted anywhere,
  // refetch the data this page shows. We listen to:
  //   - crm_changed: bulk delete + per-row employee delete
  //   - shift_update / calendar_changed: shift assignment shifts the working state
  useMessageStream((event) => {
    if (!event) return;
    if (event.type === 'crm_changed' && ['employees', 'shifts', 'paystubs', 'bookings'].includes(event.section)) {
      fetchAll();
    } else if (event.type === 'shift_update' || event.type === 'calendar_changed') {
      fetchAll();
    }
  });

  const fetchAll = async () => {
    try {
      const [empRes, payRes, timeRes, flagRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/payroll-summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/time-entries`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/time-flags`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/settings/auto-clockout`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setEmployees(empRes.data);
      setPayroll(payRes.data);
      setTimeEntries(timeRes.data);
      setFlags(flagRes.data);
      if (settingsRes?.data?.auto_clockout_hour !== undefined) {
        setAutoClockoutHour(settingsRes.data.auto_clockout_hour);
      }
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const saveAutoClockoutHour = async (h) => {
    try {
      await axios.put(`${API_URL}/api/admin/settings/auto-clockout`, { auto_clockout_hour: parseInt(h, 10) }, { withCredentials: true });
      setAutoClockoutHour(parseInt(h, 10));
      toast.success('Auto clock-out time updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteTimeEntry = async () => {
    if (!deleteEntryReason.trim()) { toast.error('Reason required'); return; }
    try {
      await axios.delete(`${API_URL}/api/admin/time-entries/${deleteEntryId}`, {
        data: { reason: deleteEntryReason.trim() },
        withCredentials: true,
      });
      toast.success('Time entry deleted');
      setDeleteEntryOpen(false); setDeleteEntryId(null); setDeleteEntryReason('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createEmployee = async () => {
    if (!name || !email) { toast.error('Name and email required'); return; }
    if (pin && !/^\d{4}$/.test(pin)) { toast.error('PIN must be exactly 4 digits (0-9)'); return; }
    if (!availability.length) { toast.error('Select at least one day of availability'); return; }
    if (!addrStreet || !addrCity || !addrProvince || !addrPostal) { toast.error('All address fields required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/employees`, {
        name, email, phone, employee_role: empRole, password,
        hourly_rate: parseFloat(hourlyRate) || 0, pin: pin || undefined,
        start_date: startDate,
        date_of_birth: dob,
        employment_type: employmentType,
        address: { street: addrStreet, city: addrCity, province: addrProvince, postal_code: addrPostal },
        availability,
      }, { withCredentials: true });
      toast.success('Employee created');
      setCreateOpen(false);
      setName(''); setEmail(''); setPhone(''); setEmpRole('staff'); setPassword('Employee123!'); setHourlyRate('15'); setPin('');
      setStartDate(todayIso()); setDob('2000-01-01'); setEmploymentType('full_time');
      setAddrStreet(''); setAddrCity(''); setAddrProvince(''); setAddrPostal('');
      setAvailability(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportEmployeesCsv = () => {
    if (!employees.length) { toast.error('No employees'); return; }
    const headers = ['Name','Email','Phone','Role','Employment Type','Start Date','Age','Tenure (months)','Hourly Rate','Availability','Street','City','Province','Postal Code','Active','PIN Set'];
    const rows = employees.map(e => [
      `"${e.name}"`, e.email, e.phone || '', ROLE_LABELS[e.employee_role] || e.employee_role,
      EMPLOYMENT_LABELS[e.employment_type] || e.employment_type || '',
      e.start_date || '', e.age != null ? e.age : '', e.tenure_months != null ? e.tenure_months : '',
      e.hourly_rate || 0,
      `"${(e.availability || []).map(d => DAY_SHORT[d]).join('/')}"`,
      `"${e.address?.street || ''}"`, `"${e.address?.city || ''}"`,
      e.address?.province || '', e.address?.postal_code || '',
      e.active ? 'Y' : 'N', e.has_pin ? 'Y' : 'N'
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `employees_${todayIso()}.csv`;
    a.click();
    toast.success('Exported');
  };

  const filteredEmployees = employees.filter(e => {
    if (filterEmpType !== 'all' && e.employment_type !== filterEmpType) return false;
    if (filterDay !== 'all' && !(e.availability || []).includes(filterDay)) return false;
    return true;
  });
  // Reset to page 1 when filters or page size change
  useEffect(() => { setPage(1); }, [filterEmpType, filterDay, pageSize]);
  const pagedEmployees = paginate(filteredEmployees, page, pageSize);

  const updateEmployee = async () => {
    if (!editEmp) return;
    if (editEmp._newPin !== undefined && editEmp._newPin !== '' && !/^\d{4}$/.test(editEmp._newPin)) {
      toast.error('PIN must be exactly 4 digits'); return;
    }
    if (editEmp.availability && editEmp.availability.length === 0) {
      toast.error('Select at least one day of availability'); return;
    }
    try {
      const data = {
        name: editEmp.name,
        employee_role: editEmp.employee_role,
        phone: editEmp.phone,
        hourly_rate: editEmp.hourly_rate,
        active: editEmp.active,
      };
      if (editEmp._newPassword) data.password = editEmp._newPassword;
      if (editEmp._newPin !== undefined && editEmp._newPin !== '') data.pin = editEmp._newPin;
      if (editEmp.start_date) data.start_date = editEmp.start_date;
      if (editEmp.date_of_birth) data.date_of_birth = editEmp.date_of_birth;
      if (editEmp.employment_type) data.employment_type = editEmp.employment_type;
      if (editEmp.address) data.address = editEmp.address;
      if (editEmp.availability) data.availability = editEmp.availability;
      await axios.patch(`${API_URL}/api/admin/employees/${editEmp.user_id}`, data, { withCredentials: true });
      toast.success('Employee updated');
      setEditOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteEmployee = async (userId) => {
    const emp = employees.find((e) => e.user_id === userId);
    const label = emp?.name || emp?.email || 'this employee';
    if (!window.confirm(`Delete ${label}? Their shifts, time entries and paystubs will be removed too. This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/admin/employees/${userId}`, { withCredentials: true });
      const c = res.data?.cascade || {};
      const cascaded = (c.shifts || 0) + (c.time_entries || 0) + (c.paystubs || 0);
      toast.success(`${label} deleted${cascaded ? ` (+${cascaded} linked records)` : ''}`);
      // Optimistic local removal so pagination + counts feel instant.
      setEmployees((prev) => {
        const next = (prev || []).filter((e) => e.user_id !== userId);
        const visibleCount = next.filter((e) => {
          if (filterEmpType !== 'all' && e.employment_type !== filterEmpType) return false;
          if (filterDay !== 'all' && !(e.availability || []).includes(filterDay)) return false;
          return true;
        }).length;
        setPage((p) => reconcilePageAfterDelete(visibleCount, p, pageSize));
        return next;
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const unlockEmployee = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/employees/${userId}/unlock`, {}, { withCredentials: true });
      toast.success('Employee unlocked');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const editTimeEntry = async () => {
    if (!editEntry || !adminReason) { toast.error('Admin reason required'); return; }
    try {
      const payload = {
        clock_in: editEntry.clock_in,
        clock_out: editEntry.clock_out,
        notes: editEntry.notes,
        booking_id: editEntry.booking_id,
        admin_reason: adminReason,
        flagged: false,
      };
      if (editEntry.break_minutes !== undefined && editEntry.break_minutes !== '') {
        payload.break_minutes = parseInt(editEntry.break_minutes, 10) || 0;
      }
      await axios.patch(`${API_URL}/api/admin/time-entries/${editEntry.entry_id}`, payload, { withCredentials: true });
      toast.success('Time entry updated');
      setEditTimeOpen(false); setAdminReason('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportTimesheetCSV = () => {
    if (!timeEntries.length) { toast.error('No entries'); return; }
    const headers = 'Employee,Role,Date,Clock In,Clock Out,Hours,Break (min),Notes,Booking,Flagged';
    const rows = timeEntries.map(e => [
      `"${e.employee_name}"`, e.employee_role, e.date,
      e.clock_in ? new Date(e.clock_in).toLocaleTimeString() : '',
      e.clock_out ? new Date(e.clock_out).toLocaleTimeString() : 'Active',
      e.hours || '', e.break_minutes || 0, `"${(e.notes || '').replace(/"/g, '""')}"`,
      e.booking_id || '', e.flagged ? 'YES' : ''
    ].join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `timesheet_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('CSV exported');
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Employees</h1>
          <p className="text-[#6B6B7A]">{employees.length} employees | {employees.filter(e => e.is_clocked_in).length} currently working</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setEmailModalOpen(true)} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="email-schedule-btn-emp">
            <Mail className="w-4 h-4 mr-2" /> Email Schedule
          </Button>
          <Button onClick={exportEmployeesCsv} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="export-employees-csv">
            <Download className="w-4 h-4 mr-2" /> Employees CSV
          </Button>
          <Button onClick={exportTimesheetCSV} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="export-timesheet">
            <Download className="w-4 h-4 mr-2" /> Timesheet CSV
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Payroll Summary */}
      {payroll && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B6B]/10 flex items-center justify-center"><Timer className="w-5 h-5 text-[#FF3B6B]" /></div>
              <div>
                <p className="text-xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{payroll.total_hours}h</p>
                <p className="text-xs text-[#6B6B7A]">Total Hours (this week)</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#06D6A0]/10 flex items-center justify-center"><DollarSign className="w-5 h-5 text-[#06D6A0]" /></div>
              <div>
                <p className="text-xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>${payroll.total_payroll?.toFixed(2)}</p>
                <p className="text-xs text-[#6B6B7A]">Payroll (this week)</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFD166]/20 flex items-center justify-center"><Users className="w-5 h-5 text-[#B8860B]" /></div>
              <div>
                <p className="text-xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{employees.filter(e => e.is_clocked_in).length}</p>
                <p className="text-xs text-[#6B6B7A]">Working Now</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-3">
        <Button onClick={() => setActiveTab('employees')} className={`rounded-full ${activeTab === 'employees' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-employees">
          <Users className="w-4 h-4 mr-1" /> Employees
        </Button>
        <Button onClick={() => setActiveTab('time')} className={`rounded-full ${activeTab === 'time' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-time-entries">
          <Clock className="w-4 h-4 mr-1" /> Time Entries
        </Button>
        <Button onClick={() => setActiveTab('availability')} className={`rounded-full ${activeTab === 'availability' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-availability">
          <CalendarDays className="w-4 h-4 mr-1" /> Availability
        </Button>
        <Button onClick={() => setActiveTab('payroll')} className={`rounded-full ${activeTab === 'payroll' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-payroll">
          <DollarSign className="w-4 h-4 mr-1" /> Batch Payroll
        </Button>
        <Button onClick={() => setActiveTab('flags')} className={`rounded-full ${activeTab === 'flags' ? 'bg-red-500 text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-flags">
          <AlertTriangle className="w-4 h-4 mr-1" /> Flags {flags?.total_flags > 0 && <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-800 rounded-full text-xs">{flags.total_flags}</span>}
        </Button>
      </div>

      {/* New consolidated Employee Control Center hint */}
      {activeTab === 'employees' && (
        <div className="rounded-xl border border-[#FF3B6B]/30 bg-[#FFF5F8] px-4 py-2 mb-4 flex items-center gap-2 text-xs text-[#6B6B7A]" data-testid="employees-hint">
          <ExternalLink className="w-3.5 h-3.5 text-[#FF3B6B] shrink-0" />
          <span>
            <b className="text-[#1E1E24]">New:</b> Click any employee's <b>name</b> to open the <b>Employee Control Center</b> — manage profile, shifts, paystubs, and messages from one page.
          </span>
        </div>
      )}

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-[#6B6B7A]">Employment Type</Label>
              <Select value={filterEmpType} onValueChange={setFilterEmpType}>
                <SelectTrigger className="w-44 mt-1 rounded-xl border-[#E8E5DF]" data-testid="filter-emp-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="full_time">Full-time</SelectItem>
                  <SelectItem value="part_time">Part-time</SelectItem>
                  <SelectItem value="casual">Casual / On-call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Available on day</Label>
              <Select value={filterDay} onValueChange={setFilterDay}>
                <SelectTrigger className="w-44 mt-1 rounded-xl border-[#E8E5DF]" data-testid="filter-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any day</SelectItem>
                  {WEEKDAYS.map(d => (
                    <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-[#6B6B7A] ml-auto">{filteredEmployees.length} of {employees.length} shown</p>
          </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <BulkSelectAllCheckbox ids={pagedEmployees.map((e) => e.user_id)} selected={bulkSelected} setSelected={setBulkSelected} testid="employees-select-all" />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Employee #</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden lg:table-cell">Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedEmployees.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">No employees match filters</TableCell></TableRow>
              ) : pagedEmployees.map(emp => (
                <TableRow key={emp.user_id}>
                  <TableCell>
                    <BulkSelectCheckbox id={emp.user_id} selected={bulkSelected} setSelected={setBulkSelected} testid={`employee-checkbox-${emp.user_id}`} />
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/crm/employees/${emp.user_id}`)}
                      className="text-left hover:text-[#FF3B6B] transition group"
                      data-testid={`employee-open-${emp.user_id}`}
                    >
                      <p className="font-medium text-[#1E1E24] group-hover:text-[#FF3B6B] group-hover:underline truncate max-w-[180px]">{emp.name}</p>
                      <p className="text-xs text-[#6B6B7A] truncate max-w-[180px]">{emp.email}</p>
                      {emp.employee_number && <p className="md:hidden text-[10px] font-mono text-[#9CA3AF]">{emp.employee_number}</p>}
                    </button>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <code className="text-xs font-semibold text-[#1E1E24]" data-testid={`employee-number-${emp.user_id}`}>{emp.employee_number || '—'}</code>
                  </TableCell>
                  <TableCell>
                    <Badge style={{ backgroundColor: `${ROLE_COLORS[emp.employee_role] || '#6B6B7A'}20`, color: ROLE_COLORS[emp.employee_role] || '#6B6B7A', border: 'none' }}>
                      {ROLE_LABELS[emp.employee_role] || emp.employee_role}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell font-medium text-sm">${emp.hourly_rate}/hr</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {emp.active ? (
                        <span className="inline-flex items-center gap-1 text-[#06D6A0] text-xs font-medium"><UserCheck className="w-3 h-3" /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#6B6B7A] text-xs"><UserX className="w-3 h-3" /> Inactive</span>
                      )}
                      {emp.is_clocked_in && <span className="px-1.5 py-0.5 bg-[#06D6A0]/10 text-[#06D6A0] rounded-full text-[10px] font-medium">Working</span>}
                      {emp.locked && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-semibold" data-testid={`locked-${emp.user_id}`}>LOCKED</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <ActionMenu
                      testid={`employee-actions-${emp.user_id}`}
                      items={[
                        { label: 'Open profile', icon: ExternalLink, onClick: () => navigate(`/crm/employees/${emp.user_id}`), testid: `open-emp-${emp.user_id}` },
                        { label: 'Edit details', icon: Pencil, onClick: () => { setEditEmp({...emp, _newPassword: '', _newPin: ''}); setEditOpen(true); }, testid: `edit-emp-${emp.user_id}` },
                        { label: 'Open chat', icon: MessageSquare, onClick: () => navigate(`/crm/messages?employee_id=${emp.user_id}`), testid: `msg-emp-${emp.user_id}` },
                        ...(emp.locked ? [{ label: 'Unlock account', icon: Key, onClick: () => unlockEmployee(emp.user_id), testid: `unlock-emp-${emp.user_id}` }] : []),
                        { separator: true },
                        { label: emp.active ? 'Delete' : 'Remove', icon: Trash2, destructive: true, onClick: () => deleteEmployee(emp.user_id), testid: `del-emp-${emp.user_id}` },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalItems={filteredEmployees.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="employees-pagination"
          itemLabel="employee"
        />
        </>
      )}

      {/* Time Entries Tab */}
      {activeTab === 'time' && (
        <>
        {/* Filters + Auto clock-out settings */}
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-[#6B6B7A]">Employee</Label>
            <Select value={filterTimeEmp} onValueChange={setFilterTimeEmp}>
              <SelectTrigger className="bg-white w-52 mt-1" data-testid="time-emp-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All employees</SelectItem>
                {employees.map(e => <SelectItem key={e.user_id} value={e.user_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B7A]">Date Range</Label>
            <Select value={timePreset} onValueChange={setTimePreset}>
              <SelectTrigger className="bg-white w-40 mt-1" data-testid="time-range-preset"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="last7">Last 7 days</SelectItem>
                <SelectItem value="thisWeek">This week (Mon–Sun)</SelectItem>
                <SelectItem value="thisMonth">This month</SelectItem>
                <SelectItem value="lastMonth">Last month</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B7A]">From</Label>
            <Input type="date" value={timeFrom} onChange={e => { setTimePreset('custom'); setTimeFrom(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="time-range-from" />
          </div>
          <div>
            <Label className="text-xs text-[#6B6B7A]">To</Label>
            <Input type="date" value={timeTo} onChange={e => { setTimePreset('custom'); setTimeTo(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="time-range-to" />
          </div>
          <div className="sm:ml-auto flex items-end gap-2">
            <div>
              <Label className="text-xs text-[#6B6B7A]">Auto clock-out at</Label>
              <Select value={String(autoClockoutHour)} onValueChange={saveAutoClockoutHour}>
                <SelectTrigger className="bg-white w-32 mt-1" data-testid="auto-clockout-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {Array.from({ length: 24 }).map((_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-[#9a9aa6] mb-3">ET</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Break</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(filterTimeEmp === 'all' ? timeEntries : timeEntries.filter(e => e.user_id === filterTimeEmp)).length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B6B7A]">No time entries yet</TableCell></TableRow>
              ) : (filterTimeEmp === 'all' ? timeEntries : timeEntries.filter(e => e.user_id === filterTimeEmp)).slice(0, 50).map(e => (
                <TableRow key={e.entry_id} className={e.flagged ? 'bg-red-50' : ''}>
                  <TableCell className="font-medium">
                    {e.employee_name}
                    {e.auto_clocked_out && (
                      <Badge className="ml-2 bg-[#FFD166]/20 text-[#B8860B] border-[#FFD166]" data-testid={`auto-badge-${e.entry_id}`}>
                        Auto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell><Badge style={{ backgroundColor: `${ROLE_COLORS[e.employee_role] || '#6B6B7A'}20`, color: ROLE_COLORS[e.employee_role] || '#6B6B7A', border: 'none' }}>{ROLE_LABELS[e.employee_role] || e.employee_role}</Badge></TableCell>
                  <TableCell>{e.date}</TableCell>
                  <TableCell>{e.clock_in ? new Date(e.clock_in).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '-'}</TableCell>
                  <TableCell>{e.clock_out ? new Date(e.clock_out).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : <span className="text-[#06D6A0] font-medium">Active</span>}</TableCell>
                  <TableCell className="font-semibold">{e.hours ? `${e.hours}h` : '-'}</TableCell>
                  <TableCell>{e.break_minutes ? `${e.break_minutes}m` : '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditEntry({...e}); setAdminReason(''); setEditTimeOpen(true); }} className="h-7 w-7 p-0" data-testid={`edit-time-${e.entry_id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setDeleteEntryId(e.entry_id); setDeleteEntryReason(''); setDeleteEntryOpen(true); }} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" data-testid={`delete-time-${e.entry_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      {e.flagged && <AlertTriangle className="w-4 h-4 text-red-500" />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      {/* Availability Calendar Tab */}
      {activeTab === 'availability' && (
        <>
          {/* Controls */}
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-[#6B6B7A]">Date Range</Label>
              <Select value={availPreset} onValueChange={setAvailPreset}>
                <SelectTrigger className="bg-white w-44 mt-1" data-testid="avail-preset"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="next7">Next 7 days</SelectItem>
                  <SelectItem value="nextWeek">Next week (Mon–Sun)</SelectItem>
                  <SelectItem value="nextMonth">Next 30 days</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">From</Label>
              <Input type="date" value={availFrom} onChange={e => { setAvailPreset('custom'); setAvailFrom(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="avail-from" />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">To</Label>
              <Input type="date" value={availTo} onChange={e => { setAvailPreset('custom'); setAvailTo(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="avail-to" />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Role</Label>
              <Select value={availRoleFilter} onValueChange={setAvailRoleFilter}>
                <SelectTrigger className="bg-white w-40 mt-1" data-testid="avail-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="party_host">Party Host</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto">
              <Button onClick={exportAvailabilityCSV} variant="outline" disabled={!availData} className="rounded-full border-[#E8E5DF]" data-testid="avail-export">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#06D6A0]" /> Available</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FFD166]" /> Busy (scheduled shift)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FF8A3D]" /> Time off</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400" /> Unavailable</span>
          </div>

          {/* Matrix */}
          {availLoading && <div className="bg-white rounded-2xl border border-[#E8E5DF] p-10 text-center text-[#6B6B7A]">Loading…</div>}
          {!availLoading && availData && (
            <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-auto" data-testid="avail-matrix">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FFFDF8]">
                  <tr>
                    <th className="px-3 py-3 text-left sticky left-0 bg-[#FFFDF8] z-10 min-w-[180px] font-semibold text-[#1E1E24]">Employee</th>
                    {availData.dates.map(d => {
                      const dt = new Date(d + 'T00:00:00');
                      return (
                        <th key={d} className="px-2 py-3 text-center font-semibold text-[#1E1E24] min-w-[60px]">
                          <div className="text-[10px] text-[#6B6B7A] uppercase">{dt.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                          <div className="text-xs">{dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {availData.employees.length === 0 && (
                    <tr><td colSpan={availData.dates.length + 1} className="text-center py-8 text-[#6B6B7A]">No employees for this role</td></tr>
                  )}
                  {availData.employees.map(row => (
                    <tr key={row.user_id} className="border-t border-[#E8E5DF]">
                      <td className="px-3 py-2 sticky left-0 bg-white z-10">
                        <p className="font-medium text-[#1E1E24]">{row.name}</p>
                        <p className="text-xs text-[#6B6B7A]">{ROLE_LABELS[row.employee_role] || row.employee_role}</p>
                      </td>
                      {row.cells.map(c => {
                        const bg = c.status === 'available' ? 'bg-[#06D6A0]/15 hover:bg-[#06D6A0]/30 text-[#06D6A0]' :
                                   c.status === 'busy' ? 'bg-[#FFD166]/30 hover:bg-[#FFD166]/50 text-[#B8860B]' :
                                   c.status === 'time_off' ? 'bg-[#FF8A3D]/25 hover:bg-[#FF8A3D]/40 text-[#c25a15]' :
                                   'bg-red-100 hover:bg-red-200 text-red-600';
                        const icon = c.status === 'available' ? <Check className="w-3 h-3" /> :
                                     c.status === 'unavailable' ? <X className="w-3 h-3" /> : null;
                        const title = c.status === 'busy'
                          ? (c.shifts || []).map(s => `${s.start_time}-${s.end_time}`).join(', ')
                          : c.status === 'time_off' ? (c.time_off?.reason || 'Time off') : '';
                        return (
                          <td key={c.date} className="p-1">
                            <button
                              title={title}
                              onClick={() => { setCellDialog({ ...row, date: c.date, cell: c }); setTimeOffReason(''); setCellDialogOpen(true); }}
                              className={`w-full h-10 rounded-md flex flex-col items-center justify-center text-xs font-semibold transition-colors ${bg}`}
                              data-testid={`avail-cell-${row.user_id}-${c.date}`}
                            >
                              {icon}
                              {c.status === 'busy' && <span className="text-[9px] uppercase">Busy</span>}
                              {c.status === 'time_off' && <span className="text-[9px] uppercase">Off</span>}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Payroll Tab */}
      {activeTab === 'payroll' && (
        <div className="space-y-5">
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-[#6B6B7A]">Employee</Label>
              <Select value={payrollEmp} onValueChange={setPayrollEmp}>
                <SelectTrigger className="bg-white w-52 mt-1" data-testid="payroll-emp-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All employees</SelectItem>
                  {employees.map(e => <SelectItem key={e.user_id} value={e.user_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Date Range</Label>
              <Select value={payrollPreset} onValueChange={setPayrollPreset}>
                <SelectTrigger className="bg-white w-40 mt-1" data-testid="payroll-preset"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="last7">Last 7 days</SelectItem>
                  <SelectItem value="thisWeek">This week</SelectItem>
                  <SelectItem value="thisMonth">This month</SelectItem>
                  <SelectItem value="lastMonth">Last month</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">From</Label>
              <Input type="date" value={payrollFrom} onChange={e => { setPayrollPreset('custom'); setPayrollFrom(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="payroll-from" />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">To</Label>
              <Input type="date" value={payrollTo} onChange={e => { setPayrollPreset('custom'); setPayrollTo(e.target.value); }} className="w-40 mt-1 rounded-xl border-[#E8E5DF]" data-testid="payroll-to" />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Role</Label>
              <Select value={payrollRoleFilter} onValueChange={setPayrollRoleFilter}>
                <SelectTrigger className="bg-white w-32 mt-1" data-testid="payroll-role-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="party_host">Party Host</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Deduction %</Label>
              <Input type="number" min="0" max="100" step="0.5" value={payrollDeduction} onChange={e => setPayrollDeduction(e.target.value)} className="w-28 mt-1 rounded-xl border-[#E8E5DF]" data-testid="payroll-deduction" />
            </div>
            <div className="ml-auto flex items-end gap-2">
              <Button variant="outline" onClick={exportPayrollCSV} disabled={!payrollData} className="rounded-full border-[#E8E5DF]" data-testid="payroll-export-csv">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
              <Button onClick={runEndOfPeriod} className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="run-end-of-period-btn">
                <DollarSign className="w-4 h-4 mr-1" /> Run End-of-Period
              </Button>
              <Button onClick={() => { const today = new Date(); const end = today.toISOString().split('T')[0]; setGenPeriodEnd(end); setGenType('weekly'); setGenNotes(''); setGenPreview(null); setGenOpen(true); }} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="generate-paystubs-btn">
                <DollarSign className="w-4 h-4 mr-1" /> Generate Paystubs
              </Button>
            </div>
          </div>

          {/* Summary tiles */}
          {payrollData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4"><p className="text-xs text-[#6B6B7A]">Employees</p><p className="text-xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{payrollData.rows?.length || 0}</p></div>
              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4"><p className="text-xs text-[#6B6B7A]">Total Hours</p><p className="text-xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{payrollData.total_hours}h</p></div>
              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4"><p className="text-xs text-[#6B6B7A]">Gross Pay</p><p className="text-xl font-bold text-[#06D6A0]" style={{ fontFamily: 'Fredoka' }}>${payrollData.total_gross?.toFixed(2)}</p></div>
              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4"><p className="text-xs text-[#6B6B7A]">Net Pay</p><p className="text-xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>${payrollData.total_net?.toFixed(2)}</p></div>
            </div>
          )}

          {/* Payroll Rows Table */}
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <div className="p-4 border-b border-[#E8E5DF] bg-[#FFFDF8]">
              <h3 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
                Payroll Breakdown {payrollFrom && payrollTo && `(${payrollFrom} → ${payrollTo})`}
              </h3>
            </div>
            {payrollLoading && <div className="p-8 text-center text-[#6B6B7A]">Computing…</div>}
            {!payrollLoading && payrollData && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Reg</TableHead>
                    <TableHead>OT (1.5x)</TableHead>
                    <TableHead>Gross</TableHead>
                    <TableHead>Ded</TableHead>
                    <TableHead>Net</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollData.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#6B6B7A]">No data for this period</TableCell></TableRow>
                  ) : payrollData.rows.map(r => (
                    <TableRow key={r.user_id} data-testid={`payroll-row-${r.user_id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: `${ROLE_COLORS[r.employee_role] || '#6B6B7A'}20`, color: ROLE_COLORS[r.employee_role] || '#6B6B7A', border: 'none' }}>{ROLE_LABELS[r.employee_role] || r.employee_role}</Badge>
                      </TableCell>
                      <TableCell>${r.hourly_rate}/hr</TableCell>
                      <TableCell>{r.regular_hours}h</TableCell>
                      <TableCell className={r.has_overtime ? 'text-[#FF3B6B] font-semibold' : ''}>{r.overtime_hours}h</TableCell>
                      <TableCell className="font-semibold text-[#06D6A0]">${r.gross_pay?.toFixed(2)}</TableCell>
                      <TableCell className="text-[#6B6B7A]">-${r.deduction_amount?.toFixed(2)}</TableCell>
                      <TableCell className="font-bold text-[#FF3B6B]">${r.net_pay?.toFixed(2)}</TableCell>
                      <TableCell>
                        {r.paid_status ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-[#06D6A0]/20 text-[#06D6A0] border-none">Paid</Badge>
                            <button onClick={() => unmarkPaid(r)} className="text-[10px] text-[#6B6B7A] hover:underline" data-testid={`unmark-paid-${r.user_id}`}>Undo</button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => markPaid(r)} className="h-7 text-xs rounded-full" data-testid={`mark-paid-${r.user_id}`}>
                            Mark Paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Paystubs List */}
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <div className="p-4 border-b border-[#E8E5DF] bg-[#FFFDF8] flex items-center justify-between">
              <h3 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Generated Paystubs</h3>
              {selectedPaystubs.length > 0 && (
                <Button onClick={sendSelectedPaystubs} className="bg-[#6366F1] hover:bg-[#4F52D4] text-white rounded-full text-xs h-8" data-testid="send-selected-paystubs">
                  <Mail className="w-3 h-3 mr-1" /> Send {selectedPaystubs.length}
                </Button>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={paystubs.length > 0 && selectedPaystubs.length === paystubs.length}
                      onCheckedChange={(v) => setSelectedPaystubs(v ? paystubs.map(p => p.paystub_id) : [])} />
                  </TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paystubs.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B6B7A]">No paystubs yet. Click "Generate Paystubs" to create.</TableCell></TableRow>
                ) : paystubs.slice(0, 100).map(ps => (
                  <TableRow key={ps.paystub_id} data-testid={`paystub-row-${ps.paystub_id}`}>
                    <TableCell>
                      <Checkbox checked={selectedPaystubs.includes(ps.paystub_id)} onCheckedChange={() => toggleSelectPaystub(ps.paystub_id)} data-testid={`select-paystub-${ps.paystub_id}`} />
                    </TableCell>
                    <TableCell className="font-medium">{ps.employee_name}</TableCell>
                    <TableCell className="text-xs">{ps.pay_period_start} → {ps.pay_period_end}</TableCell>
                    <TableCell><Badge className="bg-[#FFD166]/20 text-[#B8860B] border-none">{ps.period_type}</Badge></TableCell>
                    <TableCell>{ps.total_hours}h</TableCell>
                    <TableCell className="font-bold text-[#FF3B6B]">${ps.net_pay?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={ps.status === 'sent' || ps.status === 'viewed' ? 'bg-[#06D6A0]/20 text-[#06D6A0] border-none' : 'bg-gray-100 text-gray-700 border-none'}>{ps.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => downloadPaystubPdf(ps.paystub_id)} className="h-7 text-xs" data-testid={`download-paystub-${ps.paystub_id}`}>
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => sendPaystub(ps.paystub_id)} className="h-7 text-xs" data-testid={`send-paystub-${ps.paystub_id}`}>
                          <Mail className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Add Employee</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="font-semibold">Full Name *</Label><Input value={name} onChange={e => setName(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-name" /></div>
            <div><Label className="font-semibold">Email *</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-email" /></div>
            <div><Label className="font-semibold">Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-phone" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Role</Label>
                <Select value={empRole} onValueChange={setEmpRole}>
                  <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="party_host">Party Host</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Employment Type *</Label>
                <Select value={employmentType} onValueChange={setEmploymentType}>
                  <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-employment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-time</SelectItem>
                    <SelectItem value="part_time">Part-time</SelectItem>
                    <SelectItem value="casual">Casual / On-call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Start Date *</Label>
                <Input type="date" max={todayIso()} value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-start-date" />
              </div>
              <div>
                <Label className="font-semibold">Date of Birth *</Label>
                <Input type="date" max={todayIso()} value={dob} onChange={e => setDob(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-dob" />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Address *</Label>
              <div className="space-y-2 mt-1">
                <Input placeholder="Street address" value={addrStreet} onChange={e => setAddrStreet(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="emp-addr-street" />
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="City" value={addrCity} onChange={e => setAddrCity(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="emp-addr-city" />
                  <Input placeholder="Province" value={addrProvince} onChange={e => setAddrProvince(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="emp-addr-province" />
                  <Input placeholder="A1A 1A1" value={addrPostal} onChange={e => setAddrPostal(e.target.value.toUpperCase())} className="rounded-xl border-[#E8E5DF] font-mono" data-testid="emp-addr-postal" />
                </div>
              </div>
            </div>
            <div>
              <Label className="font-semibold">Days Available *</Label>
              <p className="text-xs text-[#6B6B7A] mb-2">Select all days the employee is regularly available</p>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(d => {
                  const selected = availability.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(setAvailability, availability, d)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-[#06D6A0] text-white border-[#06D6A0]'
                          : 'bg-white text-[#6B6B7A] border-[#E8E5DF] hover:border-[#06D6A0]'
                      }`}
                      data-testid={`emp-day-${d}`}
                    >
                      {DAY_SHORT[d]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-semibold">Hourly Rate ($)</Label><Input type="number" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-rate" /></div>
              <div><Label className="font-semibold">Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emp-password" /></div>
            </div>
            <div>
              <Label className="font-semibold">4-Digit PIN (for kiosk/header login)</Label>
              <Input
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="e.g., 1234"
                maxLength={4}
                inputMode="numeric"
                className="mt-1 rounded-xl border-[#E8E5DF] font-mono tracking-widest"
                data-testid="emp-pin"
              />
              <p className="text-xs text-[#6B6B7A] mt-1">Exactly 4 digits, stored hashed. Must be unique.</p>
            </div>
            <Button onClick={createEmployee} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-create-emp">Create Employee</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Edit Employee</DialogTitle></DialogHeader>
          {editEmp && (
            <div className="space-y-4">
              <div><Label className="font-semibold">Name</Label><Input value={editEmp.name} onChange={e => setEditEmp(p => ({...p, name: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Role</Label>
                  <Select value={editEmp.employee_role} onValueChange={v => setEditEmp(p => ({...p, employee_role: v}))}>
                    <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="party_host">Party Host</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-semibold">Employment Type</Label>
                  <Select value={editEmp.employment_type || 'full_time'} onValueChange={v => setEditEmp(p => ({...p, employment_type: v}))}>
                    <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-emp-employment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full-time</SelectItem>
                      <SelectItem value="part_time">Part-time</SelectItem>
                      <SelectItem value="casual">Casual / On-call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Start Date</Label>
                  <Input type="date" max={todayIso()} value={editEmp.start_date || ''} onChange={e => setEditEmp(p => ({...p, start_date: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-emp-start" />
                  {editEmp.tenure_months != null && <p className="text-xs text-[#6B6B7A] mt-1">Tenure: {editEmp.tenure_months >= 12 ? `${(editEmp.tenure_months/12).toFixed(1)} years` : `${editEmp.tenure_months} months`}</p>}
                </div>
                <div>
                  <Label className="font-semibold">Date of Birth</Label>
                  <Input type="date" max={todayIso()} value={editEmp.date_of_birth || ''} onChange={e => setEditEmp(p => ({...p, date_of_birth: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-emp-dob" />
                  {editEmp.age != null && <p className="text-xs text-[#6B6B7A] mt-1">Age: {editEmp.age}</p>}
                </div>
              </div>
              <div>
                <Label className="font-semibold">Address</Label>
                <div className="space-y-2 mt-1">
                  <Input placeholder="Street" value={editEmp.address?.street || ''} onChange={e => setEditEmp(p => ({...p, address: {...(p.address||{}), street: e.target.value}}))} className="rounded-xl border-[#E8E5DF]" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" value={editEmp.address?.city || ''} onChange={e => setEditEmp(p => ({...p, address: {...(p.address||{}), city: e.target.value}}))} className="rounded-xl border-[#E8E5DF]" />
                    <Input placeholder="Province" value={editEmp.address?.province || ''} onChange={e => setEditEmp(p => ({...p, address: {...(p.address||{}), province: e.target.value}}))} className="rounded-xl border-[#E8E5DF]" />
                    <Input placeholder="A1A 1A1" value={editEmp.address?.postal_code || ''} onChange={e => setEditEmp(p => ({...p, address: {...(p.address||{}), postal_code: e.target.value.toUpperCase()}}))} className="rounded-xl border-[#E8E5DF] font-mono" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="font-semibold">Days Available</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {WEEKDAYS.map(d => {
                    const selected = (editEmp.availability || []).includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setEditEmp(p => ({...p, availability: selected ? (p.availability || []).filter(x => x !== d) : [...(p.availability || []), d]}))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected ? 'bg-[#06D6A0] text-white border-[#06D6A0]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF]'
                        }`}
                        data-testid={`edit-emp-day-${d}`}
                      >
                        {DAY_SHORT[d]}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div><Label className="font-semibold">Rate ($/hr)</Label><Input type="number" value={editEmp.hourly_rate} onChange={e => setEditEmp(p => ({...p, hourly_rate: parseFloat(e.target.value)}))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div><Label className="font-semibold">Phone</Label><Input value={editEmp.phone || ''} onChange={e => setEditEmp(p => ({...p, phone: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div><Label className="font-semibold">New Password (leave empty to keep current)</Label><Input type="password" value={editEmp._newPassword} onChange={e => setEditEmp(p => ({...p, _newPassword: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div>
                <Label className="font-semibold">New PIN (leave empty to keep current)</Label>
                <Input
                  value={editEmp._newPin || ''}
                  onChange={e => setEditEmp(p => ({...p, _newPin: e.target.value.replace(/\D/g, '').slice(0, 4)}))}
                  placeholder="4 digits"
                  maxLength={4}
                  inputMode="numeric"
                  className="mt-1 rounded-xl border-[#E8E5DF] font-mono tracking-widest"
                  data-testid="edit-emp-pin"
                />
                <p className="text-xs text-[#6B6B7A] mt-1">{editEmp.has_pin ? 'PIN currently set (hidden for security).' : 'No PIN set.'}</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]">
                <span className="font-semibold text-[#1E1E24]">Active</span>
                <input type="checkbox" checked={editEmp.active} onChange={e => setEditEmp(p => ({...p, active: e.target.checked}))} />
              </div>
              <Button onClick={updateEmployee} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-edit-emp">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Flags Tab */}
      {activeTab === 'flags' && flags && (
        <div className="space-y-6">
          {/* Missing Clock-outs */}
          {flags.missing_clockouts?.length > 0 && (
            <div className="bg-white rounded-2xl border border-red-200 overflow-hidden">
              <div className="p-4 bg-red-50 border-b border-red-200"><h3 className="font-semibold text-red-700">Missing Clock-outs ({flags.missing_clockouts.length})</h3></div>
              <div className="p-4 space-y-2">
                {flags.missing_clockouts.map(e => (
                  <div key={e.entry_id} className="flex justify-between items-center p-3 bg-red-50/50 rounded-xl text-sm">
                    <div><p className="font-medium">{e.employee_name} ({e.employee_role})</p><p className="text-xs text-[#6B6B7A]">Clocked in: {e.clock_in ? new Date(e.clock_in).toLocaleString() : '-'} | {e.elapsed_hours}h elapsed</p></div>
                    <Button size="sm" onClick={() => { setEditEntry({...e}); setAdminReason(''); setEditTimeOpen(true); }} className="bg-red-500 text-white rounded-full text-xs" data-testid={`fix-${e.entry_id}`}>Fix</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Overtime */}
          {flags.overtime_alerts?.length > 0 && (
            <div className="bg-white rounded-2xl border border-yellow-200 overflow-hidden">
              <div className="p-4 bg-yellow-50 border-b border-yellow-200"><h3 className="font-semibold text-yellow-700">Overtime Alerts ({flags.overtime_alerts.length})</h3></div>
              <div className="p-4 space-y-2">
                {flags.overtime_alerts.map((a) => (
                  <div key={`${a.user_id || a.name}-${a.date || a.type}-${a.daily_hours || a.weekly_hours}`} className="flex justify-between items-center p-3 bg-yellow-50/50 rounded-xl text-sm">
                    <div><p className="font-medium">{a.name}</p><p className="text-xs text-[#6B6B7A]">{a.type === 'daily_overtime' ? `${a.date}: ${a.daily_hours}h (>8h)` : `${a.weekly_hours}h this week (>40h)`}</p></div>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">{a.type.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Other Flagged */}
          {flags.flagged_entries?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
              <div className="p-4 bg-[#FFFDF8] border-b border-[#E8E5DF]"><h3 className="font-semibold text-[#1E1E24]">Other Flagged Entries ({flags.flagged_entries.length})</h3></div>
              <div className="p-4 space-y-2">
                {flags.flagged_entries.map(e => (
                  <div key={e.entry_id} className="flex justify-between items-center p-3 bg-[#FFFDF8] rounded-xl text-sm">
                    <div><p className="font-medium">{e.employee_name}</p><p className="text-xs text-[#6B6B7A]">{e.flag_reason || 'Flagged'} | {e.date}</p></div>
                    <Button size="sm" variant="outline" onClick={() => { setEditEntry({...e}); setAdminReason(''); setEditTimeOpen(true); }} className="rounded-full text-xs">Review</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {flags.total_flags === 0 && (
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-12 text-center">
              <AlertTriangle className="w-12 h-12 text-[#06D6A0] mx-auto mb-4" />
              <p className="text-[#6B6B7A]">No flags or issues detected. Everything looks good!</p>
            </div>
          )}
        </div>
      )}

      {/* Edit Time Entry Dialog */}
      <Dialog open={editTimeOpen} onOpenChange={setEditTimeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Edit Time Entry</DialogTitle></DialogHeader>
          {editEntry && (
            <div className="space-y-4">
              <p className="text-sm text-[#6B6B7A]">{editEntry.employee_name} - {editEntry.date}</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-semibold">Clock In</Label><Input type="datetime-local" value={editEntry.clock_in?.slice(0, 16)} onChange={e => setEditEntry(p => ({...p, clock_in: e.target.value + ':00+00:00'}))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-clock-in" /></div>
                <div><Label className="font-semibold">Clock Out</Label><Input type="datetime-local" value={editEntry.clock_out?.slice(0, 16) || ''} onChange={e => setEditEntry(p => ({...p, clock_out: e.target.value ? e.target.value + ':00+00:00' : null}))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-clock-out" /></div>
              </div>
              <div>
                <Label className="font-semibold">Break (minutes)</Label>
                <Input type="number" min="0" value={editEntry.break_minutes ?? 0} onChange={e => setEditEntry(p => ({...p, break_minutes: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-break-minutes" />
              </div>
              {editEntry.auto_clocked_out && (
                <div className="bg-[#FFF8E7] rounded-xl p-3 border border-[#FFD166] text-xs text-[#B8860B]" data-testid="edit-auto-notice">
                  ⓘ This shift was auto-closed at the default cutoff. Saving will flip the Auto flag off.
                </div>
              )}
              <div><Label className="font-semibold">Notes</Label><Textarea value={editEntry.notes || ''} onChange={e => setEditEntry(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div><Label className="font-semibold">Booking ID</Label><Input value={editEntry.booking_id || ''} onChange={e => setEditEntry(p => ({...p, booking_id: e.target.value}))} className="mt-1 rounded-xl border-[#E8E5DF]" /></div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <Label className="font-semibold text-red-700">Admin Reason (required) *</Label>
                <Textarea value={adminReason} onChange={e => setAdminReason(e.target.value)} rows={2}
                  placeholder="Why is this edit being made?" className="mt-1 rounded-xl border-red-200" data-testid="admin-reason" />
              </div>
              <Button onClick={editTimeEntry} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-edit-time">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailScheduleModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />

      {/* Generate Paystubs Dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Generate Paystubs</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold">Pay Period Type</Label>
                <Select value={genType} onValueChange={setGenType}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="gen-period-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="weekly">Weekly (7 days)</SelectItem>
                    <SelectItem value="biweekly">Biweekly (14 days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Period End Date</Label>
                <Input type="date" value={genPeriodEnd} onChange={e => setGenPeriodEnd(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="gen-period-end" />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Deduction % (applies to all)</Label>
              <Input type="number" min="0" max="100" value={payrollDeduction} onChange={e => setPayrollDeduction(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" />
            </div>
            <div>
              <Label className="font-semibold">Notes (optional, applies to all)</Label>
              <Textarea rows={2} value={genNotes} onChange={e => setGenNotes(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="gen-notes" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={previewPaystubs} disabled={!genPeriodEnd} className="flex-1 rounded-full" data-testid="preview-paystubs-btn">Preview</Button>
              <Button onClick={generatePaystubs} disabled={!genPeriodEnd} className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-generate-paystubs">Generate</Button>
            </div>
            {genPreview && (
              <div className="bg-[#FFFDF8] rounded-xl p-3 border border-[#E8E5DF] text-sm max-h-60 overflow-auto">
                <p className="font-semibold mb-2">Preview: {genPreview.period_start} → {genPreview.period_end}</p>
                {genPreview.paystubs.length === 0 ? (
                  <p className="text-[#6B6B7A] text-xs">No hours in this period.</p>
                ) : genPreview.paystubs.map(p => (
                  <div key={p.paystub_id} className="flex justify-between py-1 border-b border-[#E8E5DF] last:border-0">
                    <span>{p.employee_name} ({p.total_hours}h)</span>
                    <span className="font-semibold text-[#FF3B6B]">${p.net_pay.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Availability cell dialog */}
      <Dialog open={cellDialogOpen} onOpenChange={setCellDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Day Details</DialogTitle></DialogHeader>
          {cellDialog && (
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-[#1E1E24]">{cellDialog.name}</p>
                <p className="text-sm text-[#6B6B7A]">{cellDialog.date} · {cellDialog.cell?.weekday}</p>
              </div>
              <div className="bg-[#FFFDF8] rounded-xl p-3 border border-[#E8E5DF] text-sm">
                Status: <strong className="capitalize">{cellDialog.cell?.status?.replace('_', ' ')}</strong>
                {cellDialog.cell?.status === 'busy' && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-[#6B6B7A]">Scheduled shifts:</p>
                    {(cellDialog.cell.shifts || []).map(s => (
                      <p key={s.shift_id} className="text-xs text-[#B8860B]">{s.start_time}–{s.end_time}{s.booking_id ? ` · party ${s.booking_id}` : ''}</p>
                    ))}
                  </div>
                )}
                {cellDialog.cell?.status === 'time_off' && cellDialog.cell?.time_off?.reason && (
                  <p className="mt-2 text-xs text-[#c25a15]">Reason: {cellDialog.cell.time_off.reason}</p>
                )}
              </div>

              {cellDialog.cell?.status === 'time_off' ? (
                <Button onClick={clearTimeOff} className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="clear-time-off">
                  Restore Availability
                </Button>
              ) : (
                <div className="space-y-2">
                  <Label className="font-semibold text-[#1E1E24]">Mark as Unavailable (vacation / sick)</Label>
                  <Textarea rows={2} value={timeOffReason} onChange={e => setTimeOffReason(e.target.value)}
                    placeholder="Reason (optional)" className="rounded-xl border-[#E8E5DF]" data-testid="time-off-reason" />
                  <Button onClick={markTimeOff} className="w-full bg-[#FF8A3D] hover:bg-[#e37628] text-white rounded-full" data-testid="mark-time-off">
                    Mark Unavailable
                  </Button>
                </div>
              )}

              <Button variant="outline" onClick={() => { setCellDialogOpen(false); window.location.href = '/crm/shifts'; }} className="w-full rounded-full border-[#E8E5DF] text-[#6366F1]" data-testid="assign-party-link">
                Assign to a Party (go to Shifts)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Time Entry Dialog */}
      <Dialog open={deleteEntryOpen} onOpenChange={setDeleteEntryOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Delete Time Entry?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#6B6B7A]">This will soft-delete the entry. It will no longer appear in payroll or time logs, but is preserved for audit.</p>
            <div>
              <Label className="font-semibold text-red-700">Reason (required) *</Label>
              <Textarea value={deleteEntryReason} onChange={e => setDeleteEntryReason(e.target.value)} rows={3}
                placeholder="e.g., Duplicate entry, test data, incorrect employee" className="mt-1 rounded-xl border-red-200" data-testid="delete-entry-reason" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeleteEntryOpen(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={deleteTimeEntry} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-delete-entry">Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BulkDeleteBar
        section="employees"
        selectedIds={bulkSelected}
        onClearSelection={() => setBulkSelected([])}
        onDone={() => fetchAll()}
        labelSingular="employee"
        dataTestPrefix="employees-bulk"
      />
    </div>
  );
};

export default CrmEmployees;
